import { perfTime } from '../utils/perf';
import { EventEmitter } from 'events';
import { autorun, computed, makeObservable, observable, runInAction } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
// Owning modules, never the package barrel, and never an entity CLASS as a value:
// this module is reachable from the `services` barrel, so a class value-import here
// puts every entity subclass into whatever loads a service. Type names come from
// `EntityTypes`, construction from `EntityFactory` (see entities/*-types.ts).
import { ActionInfo } from '../models/ActionInfo';
import { ExpansionRequest, QueryFilter, QueryRequest } from './query';
import type { Plugin, PluginManifest } from '../entities/plugin';
import type { Visitor } from '../entities/visitor';
import type { WebDomain } from '../entities/web-domain';
import type { AgenticProcess } from '../process/agentic-process';
import { APIEntity, dataManager, type AnyEntity } from '../APIEntity';
import type { ComputeNode } from '../entities/compute_node';
import type { Project } from '../entities/project';
import type { User } from '../entities/user';
import type { Shell } from '../entities/shell';
import type { Workspace } from '../entities/workspace';
import { TypeId } from '../models/TypeId';
import { UserWarning } from '../models/UserWarning';
import { defineGlobal } from '../utils/globals';
import { isHubOnly } from '../utils/hub-runtime';
import { isHiddenProject } from '../constants/system-projects';
import { RuntimeCaps, RuntimeInfo, RuntimeKind, runtimeCaps } from '../utils/runtime';
import { SnifferHook } from '../services/sniffer-hook';
import { HubConnectionStatus, HubLoginStatus, LocalConnectionStatus, LocalLoginStatus } from '../services/cloud_status';
import { ConnectionManager } from '../websocket';
import { AuthError, AuthEventType, authManager } from './auth';
import {
  getContextEntityFromLocalStorage,
  loadContextState,
  saveContextState,
  setContextEntityToLocalStorage,
} from './context-local-storage';
import { EntityFactory } from './factory';
import { EntityTypes } from '../schema/types';
import type { DeferredInfo } from '../models/BootstrapInfo';

/**
 * The class registered for an entity type.
 *
 * This module sits below the entity classes (it is reachable from the `services`
 * barrel), so it cannot value-import them — see the layering rule in
 * `entities/compute-node/compute-node-types.ts`. Statics are reached through the
 * registry instead, which is what `_onAddedToContext` already did for expansions.
 */
function entityClass<T>(type: string): T {
  return EntityFactory.getEntityConstructor(type) as unknown as T;
}

export enum ContextEventType {
  CONTEXT_CHANGED = 'context_changed',
}

export const UIEvents = {
  NAVIGATE_TO_ENTITY: 'navigate-to-entity',
} as const;

export type UIEventType = (typeof UIEvents)[keyof typeof UIEvents];

export interface OntologyData {
  labels: Array<{
    label: string;
    description?: string;
    color?: string;
  }>;
}

export interface PluginEntry {
  manifest?: PluginManifest;
  personalInstall?: Plugin;
  workspaceInstall?: Plugin;
}

export enum ContextEntitiesEnum {
  CurrentWorkspaceTypeId = 'CurrentWorkspaceTypeId',
  CurrentProjectTypeId = 'CurrentProjectTypeId',
  CurrentComputeNodeTypeId = 'CurrentComputeNodeTypeId',
  // Two distinct user identities — desktop (local bootstrap, HTTP-cookie auth)
  // vs cloud (Hub login). They are independent: cloud transitions must never
  // mutate the local slot, and vice versa. `someone` (the "is the dock ready
  // to render" gate) is bound to the *local* slot only.
  LocalUserTypeId = 'LocalUserTypeId',
  CloudUserTypeId = 'CloudUserTypeId',
  CurrentActiveEntityTypeId = 'CurrentActiveEntityTypeId',
  CurrentDomainTypeId = 'CurrentDomainTypeId',
  CurrentVisitorTypeId = 'CurrentVisitorTypeId',
  CurrentAgentTypeId = 'CurrentAgentTypeId',
  CurrentProcessTypeId = 'CurrentProcessTypeId',
}

/**
 * Soft-runtime-failure descriptor for the currently-rendered terminal.
 * Kept in sync with ``ProcessLoadErrorKind`` (ui/src/routes/loaders/
 * load-process.ts). The duplication keeps the SDK from importing UI types.
 */
export type TerminalRuntimeErrorKind =
  | 'runtime_terminated'
  | 'shell_entity_missing'
  | 'pty_attach_failed'
  | 'project_missing'
  | 'network_error'
  | 'project_mismatch'
  | 'failed_to_start';

export interface TerminalRuntimeError {
  kind: TerminalRuntimeErrorKind;
  processId: string;
  shellId: string | null;
  actualProjectId?: string | null;
  actualCwd?: string | null;
}

class DataContext extends EventEmitter {
  stateItem = 'flowpad-state';
  persistedState: { [key: string]: TypeId } = {};

  /**
   * Force an immediate re-report of ``browser_context`` (incl. the current
   * URL). The reporter's mobx autorun only fires on context-slot changes, so a
   * pure-URL navigation (e.g. → Home) wouldn't otherwise re-send the pathname.
   * Wired by ``startBrowserContextReporter``; a no-op until then.
   */
  resendBrowserContext: () => void = () => {};

  /** Mirror of cloudManager.isLoggedIn — only cloudManager should call setCloudLoggedIn. */
  _cloudLoggedIn = false;
  setCloudLoggedIn(v: boolean) {
    if (this._cloudLoggedIn === v) return;
    runInAction(() => {
      this._cloudLoggedIn = v;
    });
  }

  private _workspaceRevision = 0;
  private _projectRevision = 0;

  private _contextEntitiesMap = observable.map<ContextEntitiesEnum, TypeId | null | undefined>([
    [ContextEntitiesEnum.CurrentWorkspaceTypeId, null],
    [ContextEntitiesEnum.CurrentProjectTypeId, null],
    [ContextEntitiesEnum.CurrentComputeNodeTypeId, null],
    [ContextEntitiesEnum.LocalUserTypeId, null],
    [ContextEntitiesEnum.CloudUserTypeId, null],
    [ContextEntitiesEnum.CurrentActiveEntityTypeId, null],
    [ContextEntitiesEnum.CurrentDomainTypeId, null],
    [ContextEntitiesEnum.CurrentVisitorTypeId, null],
    [ContextEntitiesEnum.CurrentAgentTypeId, null],
    [ContextEntitiesEnum.CurrentProcessTypeId, null],
  ]);

  // Bootstrap state - single source of truth for UI
  // This stores errors from SDK initialization (network, auth, service unavailable, etc.)
  bootstrapError: AuthError | null = null;
  isBootstrapping: boolean = true;
  bootstrapInfo: any = null; // Store the full bootstrap response including desktop_info

  /** Merge only discovery slices; identity, paths, and live manager state stay authoritative. */
  applyInfo(info: DeferredInfo): void {
    runInAction(() => {
      const next = { ...this.bootstrapInfo };
      for (const key of ['sandbox_available', 'sandbox_compute_node', 'notice'] as const) {
        if (info[key] != null) next[key] = info[key];
      }
      if (info.desktop_info) {
        next.desktop_info = { ...next.desktop_info };
        for (const key of ['llm_providers', 'installed_agents', 'cloud_login_available'] as const) {
          if (info.desktop_info[key] != null) next.desktop_info[key] = info.desktop_info[key];
        }
      }
      this.bootstrapInfo = next;
    });
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  // User warnings - displayed in the footer status line
  _warnings: UserWarning[] = [];

  /**
   * Get the cloud API URL from bootstrap info
   */
  get cloudApiUrl(): string | null {
    return this.bootstrapInfo?.env?.cloud_api_url ?? null;
  }

  /**
   * Reactive mirror of cloudManager.isLoggedIn — cloudManager is the SSoT but
   * pushes its state into ``_cloudLoggedIn`` via ``setCloudLoggedIn`` so mobx
   * subscribers re-render. Bootstrap value comes from cloudManager too.
   */
  get cloudLoginAvailable(): boolean {
    return this._cloudLoggedIn;
  }

  // Reactive mirrors of the four orthogonal statuses. Managers push into
  // these via setCloudLoginStatus / setCloudConnectionStatus /
  // setLocalLoginStatus / setLocalConnectionStatus so mobx observers
  // re-render. Reading is sync and circular-import-safe.
  _cloudLoginStatus: HubLoginStatus = 'logged_out';
  _cloudConnectionStatus: HubConnectionStatus = 'disconnected';
  _localLoginStatus: LocalLoginStatus = 'logged_out';
  _localConnectionStatus: LocalConnectionStatus = 'disconnected';

  setCloudLoginStatus(v: HubLoginStatus) {
    if (this._cloudLoginStatus === v) return;
    runInAction(() => {
      this._cloudLoginStatus = v;
    });
  }
  setCloudConnectionStatus(v: HubConnectionStatus) {
    if (this._cloudConnectionStatus === v) return;
    runInAction(() => {
      this._cloudConnectionStatus = v;
    });
    // ``useContext`` re-reads only on CONTEXT_CHANGED: without it a reconnect left the
    // "Cloud Connection Lost" warning up until something unrelated changed the context.
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }
  setLocalLoginStatus(v: LocalLoginStatus) {
    if (this._localLoginStatus === v) return;
    runInAction(() => {
      this._localLoginStatus = v;
    });
  }
  setLocalConnectionStatus(v: LocalConnectionStatus) {
    if (this._localConnectionStatus === v) return;
    runInAction(() => {
      this._localConnectionStatus = v;
    });
  }

  get cloudLoginStatus(): HubLoginStatus {
    return this._cloudLoginStatus;
  }
  get cloudConnectionStatus(): HubConnectionStatus {
    return this._cloudConnectionStatus;
  }
  get localLoginStatus(): LocalLoginStatus {
    return this._localLoginStatus;
  }
  get localConnectionStatus(): LocalConnectionStatus {
    return this._localConnectionStatus;
  }

  /**
   * Get the desktop info from bootstrap
   */
  get desktopInfo(): any {
    return this.bootstrapInfo?.desktop_info ?? null;
  }

  /**
   * Cloud logout — delegates to cloudManager.
   */
  async cloudLogout(): Promise<void> {
    const { cloudManager } = await import('../services/cloud_login');
    await cloudManager.logout();
  }

  /**
   * The runtime the backend resolved for THIS client — the single answer to
   * "what am I running on". Read this instead of sniffing hostnames, `env_name`,
   * or `window.electronAPI`.
   *
   * The fallback is where the two bootstrap signals are reconciled, and it has
   * to be here rather than in `hub-runtime`: against a backend too old to send
   * `runtime`, `supported_pages` still identifies a hub, and defaulting such a
   * server to `desktop` would make `isHubOnly()` and this getter contradict each
   * other — the banner would read "Desktop" on the hub. Everything else old is
   * a local install, hence `desktop`.
   */
  get runtimeKind(): RuntimeKind {
    if (this.bootstrapInfo?.runtime?.kind) return this.bootstrapInfo.runtime.kind;
    return isHubOnly() ? RuntimeKind.HUB : RuntimeKind.DESKTOP;
  }

  /** What this runtime can do. Gate desktop-only features on these, not on `isHubOnly()`. */
  get caps(): RuntimeCaps {
    return runtimeCaps(this.runtimeKind);
  }

  /** The full aggregate, inputs included. For diagnostics; render off `runtimeKind`. */
  get runtime(): RuntimeInfo | null {
    return this.bootstrapInfo?.runtime ?? null;
  }

  /**
   * Backed by a flow_sdk app server rather than the hub — i.e. the full desktop
   * API surface is available (tabs, capabilities, bookmarks, local harness).
   *
   * This is what `env_name === 'desktop'` always actually meant, so the meaning
   * is unchanged: a browser tab on a local server and a cloud sandbox are both
   * "the app", and only the hub is not. It is NOT a test for the Electron shell
   * — for that, ask `runtimeKind === RuntimeKind.DESKTOP`.
   */
  get isDesktop(): boolean {
    return this.runtimeKind !== RuntimeKind.HUB;
  }

  /**
   * Get the app version from bootstrap info
   */
  get version(): string | null {
    return this.bootstrapInfo?.env?.version ?? null;
  }

  /**
   * Get the current instance name from bootstrap info (dev-mode only)
   */
  get instanceName(): string | null {
    return this.bootstrapInfo?.env?.instance_name ?? null;
  }

  /**
   * Get the current warnings list
   */
  get warnings(): UserWarning[] {
    return this._warnings;
  }

  /**
   * Add a warning to the list (replaces existing warning with same ID)
   */
  addWarning(warning: UserWarning): void {
    runInAction(() => {
      // Remove existing warning with same ID
      this._warnings = this._warnings.filter((w) => w.id !== warning.id);
      this._warnings.push(warning);
    });
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  /**
   * Remove a warning by ID
   */
  removeWarning(warningId: string): void {
    runInAction(() => {
      this._warnings = this._warnings.filter((w) => w.id !== warningId);
    });
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  /**
   * Set the entire warnings list (replaces all existing warnings)
   */
  setWarnings(warnings: UserWarning[]): void {
    runInAction(() => {
      this._warnings = warnings;
    });
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  /**
   * Clear all warnings
   */
  clearWarnings(): void {
    runInAction(() => {
      this._warnings = [];
    });
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  setSnifferHook(hook: SnifferHook | null): void {
    runInAction(() => {
      this.snifferHook = hook;
    });
  }

  setSnifferEnabled(value: boolean): void {
    runInAction(() => {
      this.snifferEnabled = value;
    });
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  setSnifferReady(value: boolean): void {
    runInAction(() => {
      this.snifferReady = value;
    });
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  /** Whether the harness settings file actually carries sniffer hooks. This —
   *  not `snifferEnabled` — is what "the sniffer is on" means to the user, so
   *  it stays true for a sniffer another instance installed. */
  setSnifferInstalled(value: boolean): void {
    runInAction(() => {
      this.snifferInstalled = value;
    });
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  /**
   * Set the active shell session ID and notify subscribers
   */
  setActiveShellId(sessionId: string): void {
    if (this.activeShellId === sessionId) return;
    runInAction(() => {
      this.activeShellId = sessionId;
    });
    let shell: Shell | null = null;
    if (sessionId) {
      try {
        shell = dataManager.getByTypeIdFromCache<Shell>(new TypeId(EntityTypes.Shell, sessionId)) ?? null;
      } catch {
        // sessionId may not yet be a valid TypeId (e.g. during init)
      }
    }
    defineGlobal('shell', shell);
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  /**
   * Canonical active terminal tab target.
   *
   * Shell tabs use shell-<id>. Agentic process tabs use
   * agentic_process-<id>. This deliberately differs from activeShellId:
   * activeShellId is the current PTY transport shell, while this field is the
   * stable row identity used by terminal tab UI.
   */
  setActiveTerminalTargetTypeId(typeId: TypeId | null): void {
    if (this.activeTerminalTargetTypeId?.equals(typeId)) return;
    runInAction(() => {
      this.activeTerminalTargetTypeId = typeId ? new TypeId(typeId) : null;
    });
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  setWorkdir(path: string | null): void {
    if (this.workdir === path) return;
    runInAction(() => {
      this.workdir = path;
    });
    defineGlobal('workdir', path);
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  /**
   * Set the soft-runtime-failure descriptor for the currently-rendered
   * terminal. Pass ``null`` on a successful load to clear any prior
   * banner. No-ops when the value matches what's already stored
   * (kind + processId equality is enough — same kind on same process
   * means same banner).
   */
  setTerminalRuntimeError(err: TerminalRuntimeError | null): void {
    const prev = this.terminalRuntimeError;
    if (
      (prev === null && err === null) ||
      (prev !== null && err !== null && prev.kind === err.kind && prev.processId === err.processId)
    ) {
      return;
    }
    runInAction(() => {
      this.terminalRuntimeError = err;
    });
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  // WebSocket connection - only set when connected
  connection: WebSocket | null = null;

  get isConnected(): boolean {
    return this.connection !== null;
  }

  // Active shell session ID - persisted across refreshes via URL
  activeShellId: string = '';

  // Active terminal tab identity. This is intentionally TypeId-based so a
  // process tab remains selected even if its current PTY shell changes.
  activeTerminalTargetTypeId: TypeId | null = null;

  // Active working directory — shown in the footer
  workdir: string | null = null;

  // Soft (non-redirecting) runtime failure for the currently-rendered
  // AgenticProcess terminal. The shell-dock route writes here when it
  // encounters a recoverable runtime problem (PTY couldn't attach, process
  // terminated, shell entity missing, project dangling, …) instead of
  // silently redirecting to a sibling. The terminal view subscribes to
  // this and renders a banner with the matching recovery action.
  // ``null`` = no runtime error; the live PTY (if any) is good.
  //
  // ``kind`` mirrors ``ProcessLoadErrorKind`` in
  // ``ui/src/routes/loaders/load-process.ts`` but is duplicated as a string
  // union here to avoid an SDK→UI import.
  terminalRuntimeError: TerminalRuntimeError | null = null;

  get activeEntityTypeId(): TypeId | null {
    return this.getContextEntityTypeId(ContextEntitiesEnum.CurrentActiveEntityTypeId);
  }

  get activeEntity(): AnyEntity | null {
    return this.getContextEntity(ContextEntitiesEnum.CurrentActiveEntityTypeId);
  }

  /**
   * "Is the desktop user known?" gate — bound to the *local* slot only.
   *
   * Cloud login is independent and must NEVER cause `someone` to flip back to
   * null. AgentLayout's loading guard reads this; we want it stable as long as
   * the local desktop has bootstrapped, regardless of cloud transitions.
   */
  get someone(): TypeId | null {
    return this.localUserTypeId || this.visitorTypeId;
  }

  activeOntology: OntologyData | null = null;
  activeLabels: string[] = [];
  manifests: PluginManifest[] = [];
  plugins: Plugin[] = [];
  snifferHook: SnifferHook | null = null;
  snifferEnabled: boolean = false;
  snifferInstalled: boolean = false;
  snifferReady: boolean = false;

  constructor() {
    super();
    makeObservable(this, {
      _cloudLoggedIn: observable,
      _cloudLoginStatus: observable,
      _cloudConnectionStatus: observable,
      _localLoginStatus: observable,
      _localConnectionStatus: observable,
      cloudApiUrl: computed,
      cloudLoginAvailable: computed,
      cloudLoginStatus: computed,
      cloudConnectionStatus: computed,
      localLoginStatus: computed,
      localConnectionStatus: computed,
      user: computed,
      workspace: computed,
      activeEntity: computed,
      activeEntityTypeId: computed,
      workspaceTypeId: computed,
      userTypeId: computed,
      localUser: computed,
      cloudUser: computed,
      currentUser: computed,
      localUserTypeId: computed,
      cloudUserTypeId: computed,
      currentUserTypeId: computed,
      projectTypeId: computed,
      computeNodeTypeId: computed,
      domainTypeId: computed,
      visitorTypeId: computed,
      project: computed,
      computeNode: computed,
      domain: computed,
      visitor: computed,
      someone: computed,
      agenticProcessTypeId: computed,
      agenticProcess: computed,
      activeShell: computed,
      activeOntology: observable,
      bootstrapError: observable,
      isBootstrapping: observable,
      bootstrapInfo: observable.ref,
      connection: observable,
      isConnected: computed,
      activeShellId: observable,
      activeTerminalTargetTypeId: observable,
      workdir: observable,
      terminalRuntimeError: observable,
      desktopInfo: computed,
      runtimeKind: computed,
      runtime: computed,
      isDesktop: computed,
      version: computed,
      instanceName: computed,
      _warnings: observable,
      warnings: computed,
      snifferHook: observable,
      snifferEnabled: observable,
      snifferInstalled: observable,
      snifferReady: observable,
    });
    this.setupAuthListeners();
    this.setupConnectionListeners();
    this.startBrowserContextReporter();
  }

  private setupAuthListeners() {
    // Listen to authManager events
    // authManager has already created/fetched entities and put them in dataManager cache
    authManager.on(AuthEventType.AUTH_STATUS_CHANGED, (user: User | null) => void this.handleAuthStatusChanged(user));
    authManager.on(AuthEventType.AUTH_ERROR, (error: AuthError) => void this.handleBootstrapError(error));
    authManager.on(AuthEventType.TOKEN_EXPIRED, () => void this.handleTokenExpired());
    authManager.on(
      AuthEventType.VISITOR_SUCCESS,
      (visitorData: { visitor_id?: string }) => void this.handleVisitorSuccess(visitorData),
    );
  }

  private async handleAuthStatusChanged(user: User | null) {
    runInAction(() => {
      this.bootstrapError = null;
      this.isBootstrapping = false;
    });

    if (user && user.typeId) {
      // User entity is already in dataManager cache. authManager owns the
      // *local* slot — cloud login goes through CloudManager into the cloud
      // slot, which is independent.
      await this.setContextEntityTypeId(ContextEntitiesEnum.LocalUserTypeId, user.typeId);
      // Clear visitor when user logs in
      await this.setContextEntityTypeId(ContextEntitiesEnum.CurrentVisitorTypeId, null);
    } else {
      // Local user logged out
      await this.setContextEntityTypeId(ContextEntitiesEnum.LocalUserTypeId, null);
    }
    // setContextEntityTypeId already emits CONTEXT_CHANGED
  }

  private handleBootstrapError(error: AuthError) {
    runInAction(() => {
      this.bootstrapError = error;
      this.isBootstrapping = false;
    });
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  private handleTokenExpired() {
    runInAction(() => {
      this.bootstrapError = null;
      this.isBootstrapping = false;
    });
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  private async handleVisitorSuccess(visitorData: { visitor_id?: string }) {
    runInAction(() => {
      this.bootstrapError = null;
      this.isBootstrapping = false;
    });

    if (visitorData.visitor_id) {
      // Visitor entity should be in dataManager cache already
      const visitorTypeId = new TypeId('visitor', visitorData.visitor_id);
      await this.setContextEntityTypeId(ContextEntitiesEnum.CurrentVisitorTypeId, visitorTypeId);
    }
    // setContextEntityTypeId already emits CONTEXT_CHANGED
  }

  private setupConnectionListeners() {
    const connectionManager = ConnectionManager.getInstance();

    // Keep the context mirror on the manager's synchronous event channel.
    // The manager used to dynamically import this module on every lifecycle
    // edge, creating an async back-reference that could outlive a reset SDK
    // realm and reject after teardown.  Initial adoption also covers a context
    // constructed after the socket has already begun connecting.
    this.setLocalConnectionStatus(connectionManager.connectionStatus);
    connectionManager.on('connection_status_changed', (slot: { status: LocalConnectionStatus }) =>
      this.setLocalConnectionStatus(slot.status),
    );

    connectionManager.on('on_open', () => {
      runInAction(() => {
        this.connection = connectionManager.getSocket();
      });
      // Expose to window when connected
      defineGlobal('connection', this.connection);
      this.emit(ContextEventType.CONTEXT_CHANGED);
    });

    connectionManager.on('on_close', () => {
      runInAction(() => {
        this.connection = null;
      });
      // Remove from window when disconnected
      defineGlobal('connection', null);
      this.emit(ContextEventType.CONTEXT_CHANGED);
    });
  }

  /**
   * Mirror this connection's data-context (every ``ContextEntitiesEnum`` slot,
   * incl. the active entity) to the backend over the UI WS, so the backend can
   * read it (``flow context list``) AND drive ``BrowserContextWatch`` (register
   * a hub watch for any ``remote`` entity in context → cross-user live updates).
   *
   * This lives in the SDK — not a React hook — so context sync works for EVERY
   * consumer (CLI, agent worker, headless test), making the SDK self-contained
   * with the backend. One-way, fire-and-forget, debounced, re-sent on every
   * (re)connect (presence stays a UI hook — it reads the DOM, this doesn't).
   */
  private startBrowserContextReporter(): void {
    const cm = ConnectionManager.getInstance();
    let lastSerialized: string | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const snapshot = (): Record<string, string | null> => {
      const out: Record<string, string | null> = {};
      for (const key of Object.values(ContextEntitiesEnum)) {
        const typeId = this.getContextEntityTypeId(key);
        out[key] = typeId ? typeId.toString() : null;
      }
      // The current route — the source of truth for "what the user is looking
      // at". Entity-context slots above go stale on non-entity pages (Home,
      // shells, lenses), so the backend matches against this pathname instead
      // (e.g. to tell whether a conversation is the open page).
      out.CurrentPathname = typeof window !== 'undefined' ? window.location.pathname : null;
      // The full address: the query carries what the path does not (viewMode, scope, focus),
      // and the backend's "you are here" (`navigation.here_from`) reads it.
      out.CurrentUrl = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : null;
      return out;
    };

    const sendNow = (force: boolean) => {
      if (!cm.connected) return;
      const ctx = snapshot();
      const serialized = JSON.stringify(ctx);
      if (!force && serialized === lastSerialized) return;
      try {
        cm.send(
          JSON.stringify({
            message_type: 'browser_context',
            message_id: uuidv4(),
            context: ctx,
          }),
        );
        lastSerialized = serialized;
      } catch {
        // Socket may have dropped; on_open re-sends forcibly.
      }
    };

    const schedule = () => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        sendNow(false);
      }, 100);
    };

    // mobx autorun fires once immediately, then on every observed change.
    // Touching each observable getter inside the closure registers it.
    autorun(() => {
      for (const key of Object.values(ContextEntitiesEnum)) {
        this.getContextEntityTypeId(key);
      }
      schedule();
    });

    const resend = () => {
      lastSerialized = null;
      sendNow(true);
    };
    this.resendBrowserContext = resend;
    cm.on('on_open', resend);
    cm.on('on_reconnected', resend);

    if (cm.connected) sendNow(true);
  }

  activeEntityTypeId2ContextEnum(typeId: TypeId): ContextEntitiesEnum | null {
    switch (typeId.type) {
      case EntityTypes.User:
        // Active-entity highlight maps to the local desktop user; cloud user
        // is informational only and handled separately by CloudManager.
        return ContextEntitiesEnum.LocalUserTypeId;
      case EntityTypes.Workspace:
        return ContextEntitiesEnum.CurrentWorkspaceTypeId;
      case EntityTypes.Project:
        return ContextEntitiesEnum.CurrentProjectTypeId;
      case EntityTypes.ComputeNode:
        return ContextEntitiesEnum.CurrentComputeNodeTypeId;
      case EntityTypes.SubAgent:
        return ContextEntitiesEnum.CurrentAgentTypeId;
      case EntityTypes.AgenticProcess:
        return ContextEntitiesEnum.CurrentProcessTypeId;
      default:
        return null;
    }
  }

  /** Alias for the local user — what callers historically meant by "the user". */
  get user(): User | null {
    return this.localUser;
  }

  /** Local desktop user (set by local bootstrap / authManager). */
  get localUser(): User | null {
    return this.getContextEntity(ContextEntitiesEnum.LocalUserTypeId) as User | null;
  }

  /** Cloud-logged-in user (set by CloudManager); independent of local user. */
  get cloudUser(): User | null {
    return this.getContextEntity(ContextEntitiesEnum.CloudUserTypeId) as User | null;
  }

  /**
   * Display-bound identity for profile UI: cloud-when-logged-in, otherwise
   * local desktop user. Use this when rendering avatars / names / emails to
   * the human. Use `localUser` for the auth principal of local API calls.
   */
  get currentUser(): User | null {
    return this.cloudUser ?? this.localUser;
  }

  get currentUserTypeId(): TypeId | null {
    return this.cloudUserTypeId ?? this.localUserTypeId;
  }

  get workspace(): Workspace | null {
    const typeId = this.getContextEntityTypeId(ContextEntitiesEnum.CurrentWorkspaceTypeId);
    if (!typeId) {
      return null;
    }
    const result = this.getContextEntity(ContextEntitiesEnum.CurrentWorkspaceTypeId) as Workspace | null;
    return result;
  }
  async _onRemovedFromContext(_entityKey: ContextEntitiesEnum, _typeId: TypeId): Promise<void> {
    // Observable update and CONTEXT_CHANGED event are handled by setContextEntityTypeId after this completes
    // No need to update observable or emit here to avoid double updates/emissions
  }
  async _onAddedToContext(_entityKey: ContextEntitiesEnum, typeId: TypeId): Promise<void> {
    // Per-step perf prints tied to the shell-nav T0 marker set by tab clicks.
    // Lets us see exactly which awaited step inside the context-set tail eats
    // the milliseconds (load vs loadHistory vs loadContextEntity).

    let entity = dataManager.getByTypeIdFromCache(typeId);
    if (!entity) {
      entity = await perfTime(`_onAddedToContext(${_entityKey}) loadContextEntity (cache miss)`, () =>
        this.loadContextEntity(typeId),
      );
    } else {
      // tests mock flows never being fetched
      await perfTime(`_onAddedToContext(${_entityKey}) entity.load (cache hit)`, () => entity!.load());
      // Check if cached entity has required expansions
      const entityConstructor = EntityFactory.getEntityConstructor(typeId.type);
      const expansionRequest = (entityConstructor as typeof APIEntity).getLoadingExpansions();
      if (!entity.isExpanded(expansionRequest)) {
        // Entity exists in cache but doesn't have required expansions, reload it
        await perfTime(`_onAddedToContext(${_entityKey}) loadContextEntity (re-expand)`, () =>
          this.loadContextEntity(typeId),
        );
      }
    }
    if (_entityKey === ContextEntitiesEnum.CurrentWorkspaceTypeId && entity) {
      defineGlobal('workspace', entity);
    }
    // Load history for process entities when added to context
    if (_entityKey === ContextEntitiesEnum.CurrentProcessTypeId && entity?.getType() === EntityTypes.AgenticProcess) {
      defineGlobal('activeProcess', entity);
      // In jsdom/Electron, window.process is the host Node process. Keep the
      // explicit app global and only preserve the legacy alias in plain browsers.
      const hostProcess = typeof window !== 'undefined' ? (window as { process?: unknown }).process : undefined;
      const hasNodeProcess =
        hostProcess &&
        typeof hostProcess === 'object' &&
        typeof (hostProcess as { listeners?: unknown }).listeners === 'function' &&
        typeof (hostProcess as { exit?: unknown }).exit === 'function';
      if (!hasNodeProcess) {
        defineGlobal('process', entity);
      }
      // History is NOT loaded here. This runs inside the route loader's context
      // write, and a full `get-history` there held the URL on the largest payload
      // the app fetches — for PTY sessions nothing even reads. Every view that
      // shows history loads it on its own mount (SimpleChatPane, VibeChatPane,
      // EntityExecutionPanel, useFlowDataTrace); docs/navigation/dock-loading.md, I3.
    }
  }

  /**
   * Is this project one that must never become the current project?
   *
   * Reads the entity (cache first, then a fetch — which the context write is
   * about to do anyway, so nothing extra is loaded in the common case). An
   * unresolvable project is NOT treated as excluded: a network failure must not
   * silently swallow a legitimate project switch.
   */
  private async isHiddenProjectId(projectTypeId: TypeId): Promise<boolean> {
    let project = dataManager.getByTypeIdFromCache<Project>(projectTypeId);
    if (!project) {
      project = await dataManager.getByTypeId<Project>(projectTypeId).catch(() => null);
    }
    return isHiddenProject(project);
  }

  async setContextEntityTypeId(entityKey: ContextEntitiesEnum, newTypeId: TypeId | null): Promise<void> {
    // App-managed projects (the Flowpad Assistant, a help-desk portal checkout)
    // are places you VISIT, never places you work: opening one of their screens
    // must leave the current project — the footer chip, the nav chip, the
    // workdir, every project-scoped action — pointing where the user actually
    // was. Rejecting here rather than in each caller is the point: this setter
    // is the ONE writer of the project context, so the rule holds for the
    // helpdesk loader, the assistant's own pages, the localStorage restore in
    // `setupProject`, and whatever opens one of them next.
    //
    // Before the revision bump, so a rejected write never cancels a real switch
    // that is still loading.
    if (
      entityKey === ContextEntitiesEnum.CurrentProjectTypeId &&
      newTypeId &&
      (await this.isHiddenProjectId(newTypeId))
    ) {
      return;
    }
    // Latest CALL wins for the project, not the latest to finish loading. Bumped
    // before the equality short-circuit: re-selecting the committed project while
    // another switch is still loading must still supersede that switch.
    const projectRevision =
      entityKey === ContextEntitiesEnum.CurrentProjectTypeId ? ++this._projectRevision : undefined;
    const existingTypeId = this._contextEntitiesMap.get(entityKey);
    if (!existingTypeId && !newTypeId) {
      return;
    }
    if (existingTypeId && existingTypeId.equals(newTypeId)) {
      return;
    }

    const workspaceRevision =
      entityKey === ContextEntitiesEnum.CurrentWorkspaceTypeId ? ++this._workspaceRevision : undefined;

    if (existingTypeId) {
      if (!newTypeId) {
        await this._onRemovedFromContext(entityKey, existingTypeId);
      } else {
        await this._onAddedToContext(entityKey, newTypeId);
        await this._onRemovedFromContext(entityKey, existingTypeId);
      }
    } else {
      await this._onAddedToContext(entityKey, newTypeId!);
    }

    if (workspaceRevision !== undefined && workspaceRevision !== this._workspaceRevision) return;
    if (projectRevision !== undefined && projectRevision !== this._projectRevision) return;

    // Update observable AFTER ensuring entity is loaded with proper expansions
    runInAction(() => {
      this._contextEntitiesMap.set(entityKey, newTypeId);
    });

    setContextEntityToLocalStorage(entityKey, newTypeId);

    if (entityKey === ContextEntitiesEnum.CurrentWorkspaceTypeId && newTypeId) {
      defineGlobal('workspace', this.workspace);
    }
    if (entityKey === ContextEntitiesEnum.CurrentProjectTypeId) {
      // The workdir belongs to the project: a switch re-points it at the new
      // project's folder. Callers with a narrower cwd (a shell, a process) set
      // it after the switch, as they already do.
      this.setWorkdir(this.project?.fs_storage_mount_path ?? null);
      // Load compute node when project is set
      if (newTypeId) {
        void this.refreshProject();
        // Open-recency stamp: `Project.last_active_at` (server clock, epoch-ms)
        // via the generic `activate` action — the project pickers' primary sort
        // key. This is the single choke point every "user is now in this
        // project" path funnels through (loaders, pickers, quick-create,
        // startup restore), and the equality guard above means it fires only on
        // an actual project switch — never on same-project re-navigation.
        // Fire-and-forget: context writes must stay fast.
        void entityClass<typeof Project>(EntityTypes.Project)
          .activateById(newTypeId.id)
          .catch(() => {});
      }
    }

    // Emit CONTEXT_CHANGED event AFTER observable update so components get the updated values
    this.emit(ContextEventType.CONTEXT_CHANGED);
  }

  getContextEntityTypeId(entityKey: ContextEntitiesEnum): TypeId | null {
    return this._contextEntitiesMap.get(entityKey) ?? null;
  }

  getContextEntity(entityKey: ContextEntitiesEnum): AnyEntity | null {
    const typeId = this.getContextEntityTypeId(entityKey);
    if (!typeId) {
      return null;
    }

    const entity = dataManager.getByTypeIdFromCache<AnyEntity>(typeId);
    return entity ?? null;
  }

  /** Alias for `localUserTypeId` — what callers historically meant by "the user TypeId". */
  get userTypeId(): TypeId | null {
    return this.localUserTypeId;
  }

  get localUserTypeId(): TypeId | null {
    return this.getContextEntityTypeId(ContextEntitiesEnum.LocalUserTypeId) ?? null;
  }

  get cloudUserTypeId(): TypeId | null {
    return this.getContextEntityTypeId(ContextEntitiesEnum.CloudUserTypeId) ?? null;
  }

  get workspaceTypeId(): TypeId | null {
    return this.getContextEntityTypeId(ContextEntitiesEnum.CurrentWorkspaceTypeId) ?? null;
  }

  get projectTypeId(): TypeId | null {
    return this.getContextEntityTypeId(ContextEntitiesEnum.CurrentProjectTypeId) ?? null;
  }

  get computeNodeTypeId(): TypeId | null {
    return this.getContextEntityTypeId(ContextEntitiesEnum.CurrentComputeNodeTypeId) ?? null;
  }

  get agentTypeId(): TypeId | null {
    return this.getContextEntityTypeId(ContextEntitiesEnum.CurrentAgentTypeId) ?? null;
  }

  get project(): Project | null {
    return this.getContextEntity(ContextEntitiesEnum.CurrentProjectTypeId) as Project | null;
  }

  get computeNode(): ComputeNode | null {
    return this.getContextEntity(ContextEntitiesEnum.CurrentComputeNodeTypeId) as ComputeNode | null;
  }

  /** Lazily-hydrated @sandbox compute node from bootstrap (E2B-backed). Null when E2B is not configured. */
  private _sandboxComputeNode: ComputeNode | null = null;
  get sandboxComputeNode(): ComputeNode | null {
    if (!this.bootstrapInfo?.sandbox_available || !this.bootstrapInfo?.sandbox_compute_node) {
      return null;
    }
    if (this._sandboxComputeNode === null) {
      this._sandboxComputeNode = EntityFactory.createEntity({
        ...(this.bootstrapInfo.sandbox_compute_node as object),
        type: EntityTypes.ComputeNode,
      }) as ComputeNode;
      this._sandboxComputeNode.markAsExpanded();
    }
    return this._sandboxComputeNode;
  }

  get domainTypeId(): TypeId | null {
    return this.getContextEntityTypeId(ContextEntitiesEnum.CurrentDomainTypeId) ?? null;
  }

  get domain(): APIEntity<WebDomain> | null {
    return this.getContextEntity(ContextEntitiesEnum.CurrentDomainTypeId);
  }

  get visitorTypeId(): TypeId | null {
    return this.getContextEntityTypeId(ContextEntitiesEnum.CurrentVisitorTypeId) ?? null;
  }

  get visitor(): APIEntity<Visitor> | null {
    return this.getContextEntity(ContextEntitiesEnum.CurrentVisitorTypeId);
  }

  get agenticProcessTypeId(): TypeId | null {
    return this.getContextEntityTypeId(ContextEntitiesEnum.CurrentProcessTypeId) ?? null;
  }

  get agenticProcess(): AgenticProcess | null {
    return this.getContextEntity(ContextEntitiesEnum.CurrentProcessTypeId) as AgenticProcess | null;
  }

  get activeShell(): Shell | null {
    if (!this.activeShellId) return null;
    return dataManager.getByTypeIdFromCache<Shell>(new TypeId(EntityTypes.Shell, this.activeShellId)) ?? null;
  }

  // @ts-ignore - Intentionally unused, reserved for future use
  private _saveStateToLocalStorage() {
    this.persistedState = saveContextState(this.persistedState, this);
  }

  // @ts-ignore - Intentionally unused, reserved for future use
  private _loadStateFromLocalStorage() {
    this.persistedState = loadContextState();
  }

  async initContext(options?: { setupWorkspace?: boolean; setupProject?: boolean }) {
    this._loadStateFromLocalStorage();
    if (options?.setupWorkspace) {
      await this.setupWorkspace();
    }
    if (options?.setupProject) {
      await this.setupProject();
    }
  }

  async getUserWorkspaces(): Promise<Workspace[]> {
    const query = new ExpansionRequest({ expand: ['permissions'] });
    const filter = new QueryFilter(query);
    const request = new QueryRequest({
      type: 'workspace',
      query: filter,
      name: 'context getUserWorkspaces query',
    });
    return await entityClass<typeof Workspace>(EntityTypes.Workspace).query(request);
  }

  async createNewUserWorkspace(): Promise<Workspace> {
    if (!this.user) {
      throw new Error('Cannot create workspace without user');
    }
    const query = new ExpansionRequest({ expand: ['permissions'] });
    const workspace = EntityFactory.createEntity({
      name: this.user.name + ' Workspace',
      type: EntityTypes.Workspace,
    }) as Workspace;
    await workspace.save();
    const expanded = await dataManager.getByTypeId<Workspace>(workspace.typeId, query);
    if (!expanded) {
      throw new Error('Failed to load new workspace');
    }
    return expanded;
  }

  async setupWorkspace() {
    if (!this.userTypeId) {
      return;
    }

    const revision = this._workspaceRevision;
    const workspaces = await this.getUserWorkspaces();
    if (revision !== this._workspaceRevision) return;

    if (workspaces.length === 0) {
      const newWorkspace = await this.createNewUserWorkspace();
      if (revision !== this._workspaceRevision) return;
      workspaces.push(newWorkspace);
    }

    // Check if there's a persisted workspace in localStorage
    const persistedWorkspaceTypeId = getContextEntityFromLocalStorage(ContextEntitiesEnum.CurrentWorkspaceTypeId);

    if (persistedWorkspaceTypeId && workspaces.some((workspace) => workspace.typeId.equals(persistedWorkspaceTypeId))) {
      await this.setContextEntityTypeId(ContextEntitiesEnum.CurrentWorkspaceTypeId, persistedWorkspaceTypeId);
    } else {
      // Fallback to personal workspace (where user is owner)
      const myWorkspace = workspaces.find((ws) => ws.ImOwner);
      if (myWorkspace) {
        await this.setContextEntityTypeId(ContextEntitiesEnum.CurrentWorkspaceTypeId, myWorkspace.typeId);
      } else if (workspaces.length > 0) {
        await this.setContextEntityTypeId(ContextEntitiesEnum.CurrentWorkspaceTypeId, workspaces[0]?.typeId);
      } else {
        await this.setContextEntityTypeId(ContextEntitiesEnum.CurrentWorkspaceTypeId, null);
      }
    }

    // Verify workspace is actually available
    const workspaceTypeId = this.getContextEntityTypeId(ContextEntitiesEnum.CurrentWorkspaceTypeId);
    const workspaceEntity = workspaceTypeId ? dataManager.getByTypeIdFromCache<Workspace>(workspaceTypeId) : null;

    // Force MobX to re-evaluate by accessing the getter
    const workspaceFromGetter = this.workspace;

    if (workspaceTypeId && !workspaceEntity) {
      await this.loadContextEntity(workspaceTypeId);
    } else if (workspaceTypeId && workspaceEntity && !workspaceFromGetter) {
      // Entity is in cache but getter returns null - likely missing required expansions
      // Wait for _onAddedToContext to finish loading with proper expansions
      const entityConstructor = EntityFactory.getEntityConstructor(workspaceTypeId.type);
      const expansionRequest = (entityConstructor as typeof APIEntity).getLoadingExpansions();
      const isExpanded = workspaceEntity.isExpanded(expansionRequest);

      if (!isExpanded) {
        await this.loadContextEntity(workspaceTypeId);
      }
    }
  }

  /**
   * Restore the remembered project (else the server's choice) into context.
   * `accept` narrows what may be restored — the UI passes "belongs to the active
   * workspace", so a workspace never wakes up in another workspace's project.
   */
  async setupProject(accept?: (project: Project) => boolean) {
    // Skip if no user logged in
    if (!this.userTypeId) {
      return;
    }

    // Check localStorage first - it has the user's last selected project
    const persistedProjectTypeId = getContextEntityFromLocalStorage(ContextEntitiesEnum.CurrentProjectTypeId);

    // If persisted project matches current, nothing to do
    if (persistedProjectTypeId && this.project?.typeId.equals(persistedProjectTypeId)) {
      return;
    }

    // Skip query if no persisted project and we already have a project from bootstrap
    if (!persistedProjectTypeId && this.project) {
      return;
    }

    // Browser memory wins when it resolves; an absent/inaccessible row falls
    // back to the server's choice. A transient failure must remain visible.
    const resolveProject = async (typeId: TypeId): Promise<Project | null> => {
      try {
        const expansions = entityClass<typeof APIEntity>(EntityTypes.Project).getLoadingExpansions();
        return await dataManager.getByTypeId<Project>(typeId, expansions);
      } catch (error) {
        const status =
          (error as { response?: { status?: number }; status?: number })?.response?.status ??
          (error as { status?: number })?.status;
        if (status === 404 || status === 403) return null;
        throw error;
      }
    };
    // An app-managed project (the assistant, a help-desk portal) is never a
    // place to RESTORE into — browser memory can still hold one from before
    // that rule existed, and it would otherwise be reinstated on every load.
    // Dropped rather than adopted, so the server's choice still gets its turn.
    const unlessHidden = (project: Project | null): Project | null =>
      project && !isHiddenProject(project) && (!accept || accept(project)) ? project : null;
    const remembered = unlessHidden(persistedProjectTypeId ? await resolveProject(persistedProjectTypeId) : null);
    const serverChoiceId = this.bootstrapInfo?.default_project?.id;
    const targetProject =
      remembered ??
      unlessHidden(serverChoiceId ? await resolveProject(new TypeId(EntityTypes.Project, serverChoiceId)) : null);

    // Nothing valid to adopt. Leaving the context alone beats picking arbitrarily:
    // whatever a loader resolves next is a better answer than a project nobody chose.
    if (!targetProject) {
      return;
    }

    await this.setContextEntityTypeId(ContextEntitiesEnum.CurrentProjectTypeId, targetProject.typeId);
  }

  private async setComputeNode(computeNode: ComputeNode | null) {
    defineGlobal('computeNode', computeNode);
    await this.setContextEntityTypeId(ContextEntitiesEnum.CurrentComputeNodeTypeId, computeNode?.typeId ?? null);
  }

  async refreshProject() {
    if (!this.project) {
      await this.setComputeNode(null);
      return;
    }
    // Compute node is always set from bootstrap — never fetch it separately.
    defineGlobal('project', this.project);
    defineGlobal('project', this.project);
  }
  async loadContextEntity(typeId: TypeId) {
    if (typeof typeId === 'string') {
      typeId = new TypeId(typeId);
    }
    const entityConstructor = EntityFactory.getEntityConstructor(typeId.type);
    const query = (entityConstructor as typeof APIEntity).getLoadingExpansions();
    const fetchEntity = await dataManager.getByTypeId(typeId, query);
    return fetchEntity ?? null;
  }
  async setActiveEntityTypeId(activeEntityTypeId: TypeId | null) {
    // Validate non-null TypeId has both type and id
    if (activeEntityTypeId !== null && (!activeEntityTypeId.type || !activeEntityTypeId.id)) {
      console.error('Invalid typeId navigation', activeEntityTypeId);
      return;
    }
    const contextEnum = activeEntityTypeId ? this.activeEntityTypeId2ContextEnum(activeEntityTypeId) : null;
    if (contextEnum) {
      await this.setContextEntityTypeId(contextEnum, activeEntityTypeId);
    }
    await this.setContextEntityTypeId(ContextEntitiesEnum.CurrentActiveEntityTypeId, activeEntityTypeId);
  }

  async refreshOntology() {
    if (!this.activeEntity) {
      this.activeOntology = null;
      this.emit(ContextEventType.CONTEXT_CHANGED);
      return;
    }

    try {
      const actionInfo = new ActionInfo('ontology', this.activeEntity.typeId.type, this.activeEntity.typeId.id, 'GET');
      const result = await dataManager.callAction(actionInfo);
      runInAction(() => {
        this.activeOntology = (result as any)?.data || this.getCurrentOntology();
      });
      this.emit(ContextEventType.CONTEXT_CHANGED);
    } catch (error) {
      console.error('Error fetching ontology, using mock data:', error);
      runInAction(() => {
        this.activeOntology = this.getCurrentOntology();
      });
      this.emit(ContextEventType.CONTEXT_CHANGED);
    }
  }

  private getCurrentOntology(): OntologyData {
    // TODO, Connect the Ontology management
    return {
      labels: [
        { label: 'task.priority.high', description: 'High priority tasks', color: '#dc2626' },
        { label: 'task.priority.medium', description: 'Medium priority tasks', color: '#f59e0b' },
        { label: 'task.priority.low', description: 'Low priority tasks', color: '#10b981' },
        { label: 'project.status.active', description: 'Active projects', color: '#3b82f6' },
        { label: 'project.status.completed', description: 'Completed projects', color: '#6b7280' },
        { label: 'project.status.on-hold', description: 'Projects on hold', color: '#f97316' },
        { label: 'feature.type.enhancement', description: 'Feature enhancements', color: '#8b5cf6' },
        { label: 'feature.type.bug-fix', description: 'Bug fixes', color: '#ef4444' },
        { label: 'feature.type.new', description: 'New features', color: '#06b6d4' },
        { label: 'team.frontend', description: 'Frontend team', color: '#14b8a6' },
        { label: 'team.backend', description: 'Backend team', color: '#f59e0b' },
        { label: 'team.design', description: 'Design team', color: '#ec4899' },
      ],
    };
  }
}

export const dataContext = new DataContext();

// Define context as a global for console debugability
defineGlobal('context', dataContext);
defineGlobal('ctx', dataContext);
defineGlobal('dataContext', dataContext);
